import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { modelService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  Trash2,
  AlertTriangle,
  Check,
  RefreshCw
} from 'lucide-react';

function Models() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const { notify } = useNotification();
  const navigate = useNavigate();

  const fetchModels = async () => {
    try {
      setLoading(true);
      const data = await modelService.getModels();

      // Ensure data is an array before setting it
      if (Array.isArray(data)) {
        setModels(data);
        console.log('Successfully loaded models:', data.length);
      } else {
        console.error('Models data is not an array:', data);
        setModels([]); // Set empty array as fallback

        if (data && typeof data === 'object' && !data.error) {
          notify.error('Invalid models data format received');
        }
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      setModels([]); // Ensure models is at least an empty array

      if (!error.message || !error.message.includes('auth')) {
        notify.error('Failed to load models. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, [notify]);

  // Filter models based on search term and status filter
  const filteredModels = models.filter(model => {
    const matchesSearch = 
      (model.model_name ? model.model_name.toLowerCase().includes(searchTerm.toLowerCase()) : false) ||
      (model.intent ? model.intent.toLowerCase().includes(searchTerm.toLowerCase()) : false) ||
      model.model_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' || 
      model.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Get clean status text
  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Ready';
      case 'baseline_deployed':
        return 'Baseline Ready';
      case 'failed':
        return 'Failed';
      case 'processing':
      case 'training':
        return 'Training';
      case 'pending':
        return 'Pending';
      default:
        return 'Unknown';
    }
  };

  // Get status styling classes
  const getStatusClasses = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'baseline_deployed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      case 'processing':
      case 'training':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  // Format date properly
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch (e) {
      return dateStr || 'Unknown';
    }
  };

  // Get display name with fallbacks
  const getDisplayName = (model) => {
    return model.model_name || model.intent || `Model ${model.model_id.slice(0, 8)}`;
  };

  // Handle model deletion
  const handleDeleteModel = async (modelId) => {
    try {
      await modelService.deleteModel(modelId);
      
      // Remove from state
      setModels(prev => prev.filter(model => model.model_id !== modelId));
      setShowDeleteConfirm(null);
      notify.success('Model deleted successfully');
      
    } catch (error) {
      console.error('Delete model error:', error);
      
      // Check if it's a CORS error - these actually succeed on the backend
      if (error.message && error.message.includes('CORS') || 
          error.message && error.message.includes('Network Error') ||
          (error.response === undefined && error.request)) {
        // CORS error but request likely succeeded - treat as success
        setModels(prev => prev.filter(model => model.model_id !== modelId));
        setShowDeleteConfirm(null);
        notify.success('Model deleted successfully');
      } else {
        notify.error('Failed to delete model');
      }
    }
  };

  // Render empty state
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
        {searchTerm || statusFilter !== 'all' ? 'No models found' : 'No models yet'}
      </h3>
      <p className="text-slate-600 dark:text-slate-400 text-center max-w-md mb-8">
        {searchTerm || statusFilter !== 'all' 
          ? 'Try adjusting your search terms or filters.' 
          : 'Create your first model to get started.'}
      </p>
      {!searchTerm && statusFilter === 'all' && (
        <Link
          to="/models/create"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 font-medium transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
        >
          Create Model
        </Link>
      )}
    </div>
  );

  // List view for models
  const renderListView = () => (
    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg">
      <div className="space-y-0">
        {filteredModels.map((model, index) => {
          if (!model || !model.model_id) return null;
          
          const displayName = getDisplayName(model);
          
          return (
            <Link
              key={model.model_id || `model-${index}`}
              to={`/models/${model.model_id}`}
              className="relative flex items-center justify-between p-4 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-200 group border-b border-slate-200 dark:border-slate-700 last:border-b-0 cursor-pointer transform hover:scale-[1.003] hover:shadow-md hover:-translate-y-0.5"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-4">
                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-slate-900 dark:text-white truncate">
                      {displayName}
                    </h3>
                  </div>
                  
                  {/* Created Date */}
                  <div className="text-sm text-slate-500 dark:text-slate-400 w-32 text-right">
                    {model.created_at ? new Date(model.created_at).toLocaleDateString() : 'Unknown'}
                  </div>
                  
                  {/* Status */}
                  <div className="w-20 flex justify-end">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClasses(model.status)}`}
                      title={
                        model.status === 'pending'
                          ? 'Due to high traffic, model builds may be queued and take a few hours'
                          : model.status === 'baseline_deployed'
                          ? 'Baseline model deployed - you can make predictions now while the final model is being trained'
                          : ''
                      }
                    >
                      {getStatusText(model.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center ml-6">
                <button
                  type="button"
                  className={`opacity-0 group-hover:opacity-100 transition-all duration-200 p-1 transform hover:scale-110 ${
                    showDeleteConfirm === model.model_id
                      ? 'text-red-500 hover:text-red-600'
                      : 'text-slate-400 hover:text-red-500'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (showDeleteConfirm === model.model_id) {
                      // Second click - actually delete
                      handleDeleteModel(model.model_id);
                    } else {
                      // First click - show confirm state
                      setShowDeleteConfirm(model.model_id);
                      // Auto-reset after 3 seconds if not clicked
                      setTimeout(() => {
                        setShowDeleteConfirm(null);
                      }, 3000);
                    }
                  }}
                  title={showDeleteConfirm === model.model_id ? "Click again to confirm delete" : "Delete model"}
                >
                  {showDeleteConfirm === model.model_id ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );


  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-white">Models</h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Manage and monitor your machine learning models
            </p>
          </div>
          <Link
            to="/models/create"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 font-medium transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
          >
            Create Model
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex gap-3 items-center">
        <div className="flex-1">
          <input
            type="text"
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600 focus:border-transparent"
            placeholder="Search models..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="p-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg transform hover:scale-110 transition-all duration-200"
          onClick={() => fetchModels()}
          disabled={loading}
          title="Refresh models"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="completed">Ready</option>
          <option value="processing">Training</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Models content */}
      {loading ? (
        <div className="py-12 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-slate-300 dark:border-slate-600 border-t-slate-600 dark:border-t-slate-300 rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      ) : filteredModels.length > 0 ? (
        renderListView()
      ) : (
        renderEmptyState()
      )}
    </div>
  );
}

export default Models;